import type {
  Answer,
  AnswerValue,
  Quiz,
  QuizMark,
} from '@efdigital/ll-addon-data';
import { Marker } from '@efdigital/ll-admin-v2/utils/mark-answer';
import { generateRandomAnswerValues } from '@efdigital/ll-admin-v2/utils/random-answer';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { cell, resource, use } from 'ember-resources';

import { type Focus, type View } from './quiz-edit-mode.gts';

const marker = new Marker();

export type EditQuizContextState = {
  focus: Focus;
  view: View;
  isolatedIndex: number;
  isolate: (index: number) => void;
  isIsolated: boolean;
  quiz: Quiz;
  answer: Answer;
  setFocus: (focus: Focus) => void;
  setView: (view: View) => void;
};

export class EditQuizContext extends Component<{
  Args: {
    quiz: Quiz;
    answers: Answer;
  };
  Blocks: {
    default: [quizContext: EditQuizContextState];
  };
}> {
  @tracked focus: Focus = 'default';
  @tracked view: View = 'input';
  @tracked isolatedIndex = -1;

  cachedValues: AnswerValue[] = [];

  setFocus = (focus: Focus) => {
    this.focus = focus;
  };

  setView = (view: View) => {
    this.view = view;
  };

  @use answerValues = resource<AnswerValue[]>(() => {
    const knownValues = cell<AnswerValue[]>([]);

    const { values, hasChanges } = generateRandomAnswerValues(
      this.args.quiz.questions || [],
      this.cachedValues
    );
    if (hasChanges) {
      knownValues.set(values);
      this.cachedValues = values;
    }

    return () => knownValues.current;
  });

  get answer(): Answer {
    const values = [...this.answerValues];
    return {
      ...this.args.answers,
      values,
      mark: marker.mark(this.args.quiz, {
        ...this.args.answers,
        values,
      }) as QuizMark,
    };
  }

  isolate = (index: number) => {
    this.isolatedIndex = index;
  };

  get isIsolated(): boolean {
    return (
      this.isolatedIndex >= 0 &&
      this.isolatedIndex < (this.args.quiz.questions || []).length
    );
  }

  get state(): EditQuizContextState {
    return {
      focus: this.focus,
      view: this.view,
      isolatedIndex: this.isolatedIndex,
      isolate: this.isolate,
      isIsolated: this.isIsolated,
      quiz: this.args.quiz,
      answer: this.answer,
      setFocus: this.setFocus,
      setView: this.setView,
    };
  }

  <template>{{yield this.state}}</template>
}
